/**
 * Блок «Видео» вкладки «Договор»: события сделки из CRM (примерка, печать договора, оплата, выдача,
 * возврат) с камерами салона и ролики сделки (история — собранные и в этом окне, и раньше). Клик по
 * событию — окно просмотрщика во весь экран на моменте события: все камеры салона (камеры события
 * выбраны), события сделки — метками на шкале. «Все камеры салона» — то же окно без события (сетка
 * всех камер салона сделки), даже если событий у сделки нет; в окне можно сменить салон и дату.
 * Только с правом `video_view`. В панели расширения кадры не грузятся (нужна cookie кабинета) — там
 * ссылки на экран «Видео» кабинета. Спека `docs/specs/2026-10-23-video-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, LayoutGrid, Video, X } from "lucide-react";
import { useEffect, useState } from "react";

import { type ClipItem, ClipList } from "@/components/video/ClipList";
import { VideoStudio } from "@/components/video/VideoStudio";
import { VIDEO_VIEW, useCan } from "@/lib/access";
import { query } from "@/lib/api";
import { type DealVideoEvent, dayOf, dealVideoQuery, hms, shortDay, todayMsk, videoCitiesQuery, videoClipsQuery } from "@/lib/video";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]";
const control = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";

/** Что открыть в окне: событие сделки или «все камеры салона» (без камер события и отрезка). */
type Target = { title: string; at: string; from: string | null; to: string | null; location_id: number | null; cameras: { id: number; name: string }[] };

const ofEvent = (e: DealVideoEvent): Target => ({ title: e.title, at: e.at, from: e.from, to: e.to, location_id: e.location_id, cameras: e.cameras });

export const videoPageUrl = (t: Target, base = "") =>
  `${base}/video${query({
    location: t.location_id,
    camera: t.cameras[0]?.id,
    cams: t.cameras.map((c) => c.id).join(",") || undefined,
    view: t.cameras.length ? "one" : "grid",
    at: t.at,
    from: t.from,
    to: t.to,
  })}`;

function VideoDialog({ t, events, chatId, dealId, onClose }: { t: Target; events: DealVideoEvent[]; chatId: number | null; dealId: number; onClose: () => void }) {
  const cities = useQuery(videoCitiesQuery());
  const [loc, setLoc] = useState<number | null>(t.location_id);
  const [day, setDay] = useState(dayOf(t.at));
  useEffect(() => {
    const onKey = (k: KeyboardEvent) => k.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  useEffect(() => {
    if (loc == null && cities.data?.[0]) setLoc(cities.data[0].id);
  }, [loc, cities.data]);
  // Сменили салон или день — событие уже не про них: сетка всех камер на 12:00.
  const same = loc === t.location_id && day === dayOf(t.at);
  const cams = same ? t.cameras : [];
  const at = same ? t.at : `${day}T12:00:00`;
  const page: Target = { ...t, location_id: loc, at, cameras: cams, from: same ? t.from : null, to: same ? t.to : null };
  return (
    <div className="fixed inset-0 z-50 flex bg-black/60 sm:p-3" onClick={onClose}>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-bg text-fg sm:rounded-[12px]" onClick={(x) => x.stopPropagation()} data-annot="окно видео события">
        <div className="flex flex-none flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
          <div className="min-w-0 text-[15px] font-medium">
            {t.title}
            {t.cameras.length > 0 && (
              <span className="text-fg-muted">
                {" "}
                · {shortDay(t.at)} {hms(t.at)}
              </span>
            )}
          </div>
          <select value={loc ?? ""} onChange={(e) => setLoc(Number(e.target.value))} className={control} title="Салон" data-annot="салон видео">
            {cities.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {c.cameras}
              </option>
            ))}
          </select>
          <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className={control} title="День" />
          <span className="flex-1" />
          <a href={videoPageUrl(page)} target="_blank" rel="noreferrer" title="Открыть в разделе «Видео»" className="grid size-8 place-items-center rounded-full hover:bg-bg-subtle">
            <ExternalLink size={16} />
          </a>
          <button type="button" onClick={onClose} title="Закрыть (Esc)" className="grid size-8 place-items-center rounded-full hover:bg-bg-subtle">
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {loc == null ? (
            <div className="text-fg-muted">{cities.isError ? `Салоны не загрузились: ${cities.error.message}` : "Загружаю салоны…"}</div>
          ) : (
            <VideoStudio
              key={`${loc}-${day}`}
              locationId={loc}
              cameras={cams}
              eventCameras={cams.map((c) => c.id)}
              selected={cams.map((c) => c.id)}
              camera={cams[0]?.id ?? null}
              at={at}
              from={page.from}
              to={page.to}
              view={cams.length ? "one" : "grid"}
              events={events}
              chatId={chatId}
              dealId={dealId}
              caption={cams.length ? `Видео: ${t.title.toLowerCase()} ${shortDay(t.at)} ${hms(t.at).slice(0, 5)}` : `Видео ${shortDay(at)}`}
              autoFocus
            />
          )}
        </div>
      </div>
    </div>
  );
}

function DealClips({ dealId, chatId }: { dealId: number; chatId: number | null }) {
  const q = useQuery(videoClipsQuery({ deal: dealId }));
  if (!q.data?.length) return null;
  const items: ClipItem[] = q.data.map((r) => ({
    id: r.key,
    camera: r.camera,
    from: r.from,
    to: r.to,
    status: r.status,
    key: r.status === "ready" ? r.key : undefined,
    size: r.size,
    error: r.error,
    user: r.user,
  }));
  return (
    <div className="mt-3 flex flex-col gap-1.5" data-annot="ролики сделки">
      <div className="font-medium">Ролики · {items.length}</div>
      <ClipList items={items} chatId={chatId} dealId={dealId} caption="Видео по вашей сделке" />
    </div>
  );
}

function Body({ dealId, chatId, base, open, setOpen }: { dealId: number; chatId: number | null; base: string; open: Target | null; setOpen: (t: Target | null) => void }) {
  const q = useQuery(dealVideoQuery(dealId));
  if (q.isPending) return <div className="text-fg-muted">Ищу события в CRM…</div>;
  if (q.isError) return <div className="text-err">События не загрузились: {q.error.message}</div>;
  return (
    <>
      {q.data.events.length === 0 ? (
        <div className="text-fg-muted">У сделки нет событий у стойки: примерки, договора, оплаты, выдачи. Архив камер — кнопкой «Все камеры салона».</div>
      ) : (
        <ul className="flex flex-col">
          {q.data.events.map((e) => {
            const text = (
              <>
                <span className="w-[86px] flex-none tabular-nums text-fg-muted">
                  {shortDay(e.at)} {hms(e.at).slice(0, 5)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{e.title}</span>
                  {e.detail && <span className="text-fg-muted"> · {e.detail}</span>}
                  {e.user && <span className="text-fg-muted"> · {e.user}</span>}
                  <span className="block truncate text-[11.5px] text-fg-muted">
                    {e.cameras.length ? e.cameras.map((c) => c.name).join(", ") : e.location_id != null ? "камеры события не привязаны — все камеры салона" : "камер Macroscop нет"}
                  </span>
                </span>
              </>
            );
            const row = "flex w-full items-start gap-2 rounded px-1 py-1 text-left";
            if (e.cameras.length === 0 && e.location_id == null)
              return (
                <li key={`${e.kind}-${e.at}`} className={`${row} opacity-60`}>
                  {text}
                </li>
              );
            return (
              <li key={`${e.kind}-${e.at}`}>
                {base ? (
                  <a href={videoPageUrl(ofEvent(e), base)} target="_blank" rel="noreferrer" className={`${row} hover:bg-bg`}>
                    {text}
                    <ExternalLink size={14} className="mt-0.5 flex-none text-accent-text" />
                  </a>
                ) : (
                  <button type="button" onClick={() => setOpen(ofEvent(e))} className={`${row} hover:bg-bg`}>
                    {text}
                    <Video size={14} className="mt-0.5 flex-none text-accent-text" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!base && <DealClips dealId={dealId} chatId={chatId} />}
      {open && <VideoDialog t={open} events={q.data.events} chatId={chatId} dealId={dealId} onClose={() => setOpen(null)} />}
    </>
  );
}

export function DealVideo({ dealId, chatId = null, base = "" }: { dealId: number; chatId?: number | null; base?: string }) {
  const can = useCan(VIDEO_VIEW);
  const [shown, setShown] = useState(true);
  const [open, setOpen] = useState<Target | null>(null);
  const q = useQuery(dealVideoQuery(dealId, can));
  if (!can) return null;
  // «Все камеры салона»: салон сделки (договор, иначе последний приход), день — последнего события.
  const events = q.data?.events ?? [];
  const last = events.at(-1);
  const all: Target = {
    title: "Все камеры салона",
    at: `${last ? dayOf(last.at) : todayMsk()}T12:00:00`,
    from: null,
    to: null,
    location_id: q.data?.location_id ?? events.find((e) => e.location_id != null)?.location_id ?? null,
    cameras: [],
  };
  const allBtn = "inline-flex h-7 flex-none items-center gap-1 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12px] font-normal hover:bg-bg-subtle";
  return (
    <div className={card} data-annot="видео сделки">
      <div className="flex items-center gap-1.5">
        <button type="button" onClick={() => setShown(!shown)} className="flex min-w-0 flex-1 items-center gap-1.5 text-left font-medium">
          <Video size={15} className="text-accent-text" /> Видео
          <span className="ml-1 truncate text-[11.5px] font-normal text-fg-muted">{shown ? "свернуть" : "примерка, договор, оплата, выдача"}</span>
        </button>
        {base ? (
          <a href={videoPageUrl(all, base)} target="_blank" rel="noreferrer" className={allBtn} title="Архив всех камер салона на любой день">
            <LayoutGrid size={13} /> Все камеры салона
          </a>
        ) : (
          <button type="button" onClick={() => setOpen(all)} className={allBtn} title="Архив всех камер салона на любой день — даже если событий нет">
            <LayoutGrid size={13} /> Все камеры салона
          </button>
        )}
      </div>
      {shown && (
        <div className="mt-2">
          <Body dealId={dealId} chatId={chatId} base={base} open={open} setOpen={setOpen} />
        </div>
      )}
      {!shown && open && <VideoDialog t={open} events={events} chatId={chatId} dealId={dealId} onClose={() => setOpen(null)} />}
    </div>
  );
}
